import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { BrandLogo } from "@/components/BrandLogo";
import LegalFooter from "@/components/LegalFooter";
import { LEGAL, getLegal, isRTL } from "@/lib/legal";
import { useAuth } from "@/lib/auth";
import { getUser } from "@/lib/session";

export default function TermsOfService() {
  const { account } = useAuth();
  const initial = account?.language || getUser()?.language || "English";
  const [language, setLanguage] = useState(LEGAL[initial] ? initial : "English");
  const t = getLegal(language);
  const dir = isRTL(language) ? "rtl" : "ltr";

  return (
    <div className="min-h-[100dvh] bg-[#060817] text-[#E4E4E7] flex flex-col" data-testid="terms-page">
      <header className="flex items-center justify-between px-5 py-4 border-b border-white/5">
        <Link to="/" className="flex items-center gap-2 text-[#A0A0AB] hover:text-white transition-colors" data-testid="terms-back">
          <ArrowLeft className="w-4 h-4" /> <BrandLogo className="text-lg" />
        </Link>
        <select data-testid="terms-language" value={language} onChange={(e) => setLanguage(e.target.value)}
          className="bg-[#0F1636] border border-white/10 rounded-full px-3 py-1.5 text-sm text-[#F1F1F5] outline-none focus:border-[#00D9FF]">
          {Object.keys(LEGAL).map((l) => <option key={l} value={l}>{l}</option>)}
        </select>
      </header>

      <main dir={dir} className="flex-1 w-full max-w-3xl mx-auto px-5 py-10">
        <h1 className="text-3xl sm:text-4xl font-display font-black text-[#F1F1F5] mb-2">{t.terms}</h1>
        <p className="text-xs text-[#71717A] mb-8">Last updated: June 2026</p>

        {/* Localized key clause */}
        <section className="bg-amber-500/5 border border-amber-500/25 rounded-2xl p-5 mb-8" data-testid="terms-clause">
          <h2 className="text-lg font-bold text-amber-300 mb-3">{t.noticeTitle}</h2>
          {t.clause.map((p, i) => (
            <p key={`clause-${p.slice(0, 32)}-${i}`} className="text-sm text-[#C7C7CF] leading-relaxed mb-2">{p}</p>
          ))}
        </section>

        <div className="space-y-6 text-sm leading-relaxed text-[#C7C7CF]">
          <div>
            <h3 className="text-base font-bold text-[#F1F1F5] mb-2">1. Acceptance of Terms</h3>
            <p>By creating an account, joining a room as a guest, or otherwise using Ibeflix, you agree to these Terms of Service. If you do not agree, do not use Ibeflix.</p>
          </div>
          <div>
            <h3 className="text-base font-bold text-[#F1F1F5] mb-2">2. Legal Streaming Only</h3>
            <p>Ibeflix is a synchronization and chat tool for watching content together on <strong>legal</strong> streaming services (YouTube, Netflix, Disney+, Hulu, Tubi, Pluto TV, and similar). Ibeflix does not host, store, or stream any video content itself.</p>
          </div>
          <div>
            <h3 className="text-base font-bold text-[#F1F1F5] mb-2">3. Your Responsibilities</h3>
            <p>You are solely responsible for ensuring you have the legal right to access and share any content or link you bring into a room. You must comply with the terms of the streaming services you use and with all applicable copyright laws.</p>
          </div>
          <div>
            <h3 className="text-base font-bold text-[#F1F1F5] mb-2">4. Prohibited Content</h3>
            <p>You may not share links to pirated or infringing content (for example skyflix.to, 123movies, putlocker, fmovies, and similar sites), nor use Ibeflix to facilitate copyright infringement, harassment, hate speech, or any illegal activity.</p>
          </div>
          <div>
            <h3 className="text-base font-bold text-[#F1F1F5] mb-2">5. Enforcement</h3>
            <p>We may remove content, suspend, or permanently ban users who violate these terms, with or without notice. Users may report violations using the “Report Abuse” link in the footer.</p>
          </div>
          <div>
            <h3 className="text-base font-bold text-[#F1F1F5] mb-2">6. Disclaimer</h3>
            <p>Ibeflix is provided “as is” without warranties of any kind. We are not responsible for content accessed through third-party services, nor for any user’s misuse of the tool.</p>
          </div>
          <div>
            <h3 className="text-base font-bold text-[#F1F1F5] mb-2">7. Changes</h3>
            <p>We may update these terms from time to time. Continued use of Ibeflix after changes means you accept the updated terms.</p>
          </div>
          <div>
            <h3 className="text-base font-bold text-[#F1F1F5] mb-2">8. Contact</h3>
            <p>Questions? Email <a href="mailto:support@ibeflix.com" className="text-[#00D9FF] underline">support@ibeflix.com</a>.</p>
          </div>
        </div>
      </main>

      <LegalFooter language={language} />
    </div>
  );
}
